import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Avatar, Button, Chip, Grid, MenuItem, Stack, Tab, Tabs, TextField,
  Typography, FormControlLabel, Switch, Box, FormHelperText, alpha,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import FormFileUpload from '../ui/forms/FormFileUpload';
import PersonNameField from '../ui/forms/PersonNameField';
import PhoneWithDialCodeField from '../ui/forms/PhoneWithDialCodeField';
import FormDrawer from '../ui/FormDrawer';
import ConfirmDialog from '../ui/ConfirmDialog';
import PartnerDocumentsPanel from '../partners/PartnerDocumentsPanel';
import ZoneMasterSelect from '../masters/ZoneMasterSelect';
import { getMasters } from '../../services/platform/masterService';
import { listPartners, getPartnerDocuments, type PartnerRow } from '../../services/partnerUserService';
import { listPartnerSubscriptionPlans } from '../../services/partnerSubscriptionPlanService';
import { resolveAssetUrl } from '../../utils/assetUrl';
import {
  masterImageUrl, resolveRestaurantImagesFromMasters, openingHoursFromMasters,
  type OpeningHourRow, type FoodMasterRow,
} from '../../utils/foodMasterImages';
import {
  emptyRestaurantForm, restaurantRowToForm, restaurantFormToPayload,
  RESTAURANT_MASTER_TABS, VERIFICATION_STATUS_OPTIONS, WEEK_DAYS,
  findRestaurantDuplicateErrors, normalizeRestaurantSlug,
  type RestaurantFormState,
} from './restaurantMasterTypes';
import RestaurantLocationPicker from './RestaurantLocationPicker';
import {
  extractMerchantCompliance,
  extractApprovedDocumentCompliance,
  getMerchantDocumentAvailability,
  hasMerchantComplianceNumbers,
  formatMerchantDocumentStatus,
  merchantDocumentStatusColor,
  buildApprovedComplianceUpdates,
  buildChangedComplianceUpdates,
  type MerchantDocumentSlot,
} from '../../utils/merchantComplianceExtract';
import { chargeFieldLabel, formatMoney, getCurrencySymbol } from '../../utils/currency';
import { validateLocalPhoneForCountry, validateEmail } from '../../utils/personFieldValidation';
import { phoneLengthForDialCode } from '../../services/countryDialCodeService';
import { localPhoneDigits } from '../../utils/phoneDialCode';
import { welcomeEmailConfig, welcomeEmailHelper } from '../../config/welcomeEmail';
import { getWelcomeEmailConfig } from '../../services/welcomeEmailService';
import {
  hasRestaurantOpeningHourErrors,
  openingHoursValidationSummary,
  validateRestaurantOpeningHours,
} from '../../utils/restaurantOpeningHours';
import {
  tabErrorCounts,
  validateCommissionValue,
  commissionValueHelperText,
  validateRestaurantForm,
  validateRestaurantName,
  validateRestaurantMinOrderAmount,
  RESTAURANT_MIN_ORDER_MIN,
  RESTAURANT_MIN_ORDER_MAX,
} from '../../utils/restaurantFormValidation';
import { resolveRestaurantComplianceRequirements } from '../../utils/restaurantComplianceRequirements';
import { isMasterRecordSelectable } from '../../utils/masterRecordStatus';

const readableAlertSx = {
  '& .MuiAlert-message': { color: 'text.primary', fontWeight: 600, width: '100%' },
} as const;

/** High-contrast info callout — theme info alerts can render too faint in some tenants. */
const highContrastInfoSx = {
  bgcolor: '#e0f2fe',
  color: '#0c4a6e',
  border: '1px solid',
  borderColor: '#0284c7',
  '& .MuiAlert-icon': { color: '#0369a1' },
  '& .MuiAlert-message': {
    color: '#0c4a6e !important',
    fontWeight: 700,
    width: '100%',
  },
  '& .MuiAlert-message .MuiTypography-root': {
    color: '#0c4a6e !important',
    fontWeight: 700,
  },
  '& strong': { color: '#082f49', fontWeight: 800 },
} as const;

export type { RestaurantFormState };
export { emptyRestaurantForm, restaurantRowToForm, restaurantFormToPayload };

async function loadFoodMasters(type: string): Promise<FoodMasterRow[]> {
  const res = await getMasters(type, { limit: 300, activeOnly: true });
  return ((res.data || []) as FoodMasterRow[]).filter(isMasterRecordSelectable);
}

interface RestaurantAddDrawerProps {
  open: boolean;
  editing: Record<string, unknown> | null;
  form: RestaurantFormState;
  onFormChange: (next: RestaurantFormState) => void;
  onClose: () => void;
  onSubmit: () => void;
  loading?: boolean;
  errorMessage?: string | null;
  serverFieldErrors?: Record<string, string>;
  existingRestaurants?: Array<{ _id?: string; name?: string; slug?: string }>;
}

export default function RestaurantAddDrawer({
  open, editing, form, onFormChange, onClose, onSubmit, loading, errorMessage,
  serverFieldErrors = {}, existingRestaurants = [],
}: RestaurantAddDrawerProps) {
  const [tab, setTab] = useState(0);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [complianceApplyNotice, setComplianceApplyNotice] = useState<{ severity: 'success' | 'warning' | 'error' | 'info'; message: string } | null>(null);
  const [applyComplianceConfirmOpen, setApplyComplianceConfirmOpen] = useState(false);
  const [merchantDocumentsOpen, setMerchantDocumentsOpen] = useState(false);
  const [touched, setTouched] = useState<{
    name?: boolean; slug?: boolean; phone?: boolean; supportPhone?: boolean; email?: boolean; description?: boolean;
    commission?: boolean;
    openingHours?: Record<number, boolean>;
  }>({});
  const patch = (partial: Partial<RestaurantFormState>) => onFormChange({ ...form, ...partial });

  const { data: welcomeConfig } = useQuery({
    queryKey: ['welcome-email-config'],
    queryFn: getWelcomeEmailConfig,
    enabled: open,
    staleTime: 60_000,
  });

  const { data: masters, isLoading: mastersLoading } = useQuery({
    queryKey: ['restaurant-add-masters'],
    queryFn: async () => ({
      restaurant_types: await loadFoodMasters('restaurant_types'),
      cuisine_types: await loadFoodMasters('cuisine_types'),
      restaurant_branches: await loadFoodMasters('restaurant_branches'),
      restaurant_opening_hours: await loadFoodMasters('restaurant_opening_hours'),
      merchant_document_types: await loadFoodMasters('merchant_document_types'),
    }),
    enabled: open,
  });

  const complianceRequired = useMemo(
    () => resolveRestaurantComplianceRequirements(masters?.merchant_document_types),
    [masters?.merchant_document_types],
  );

  const { data: merchants = [], isFetched: merchantsFetched, isFetching: merchantsFetching } = useQuery({
    queryKey: ['merchant-partners-select'],
    queryFn: async () => {
      const res = await listPartners({ entityType: 'merchant', limit: 200 });
      return res.data;
    },
    enabled: open,
    staleTime: 60_000,
  });

  const merchantsReady = !open || (merchantsFetched && !merchantsFetching);

  const merchantOptions = useMemo(() => {
    const byId = new Map(merchants.map((m) => [m._id, m]));
    const seen = new Set<string>();
    const out: { id: string; label: string }[] = [];

    const push = (id: string, label: string) => {
      if (!id || seen.has(id)) return;
      seen.add(id);
      out.push({ id, label });
    };

    if (form.merchantId) {
      const linked = byId.get(form.merchantId);
      push(
        form.merchantId,
        linked
          ? `${linked.firstName} ${linked.lastName || ''} · ${linked.phone || linked.email}`
          : `Linked merchant (${form.merchantId.slice(-6)})`,
      );
    }
    for (const m of merchants) {
      push(m._id, `${m.firstName} ${m.lastName || ''} · ${m.phone || m.email}`);
    }
    return out;
  }, [form.merchantId, merchants]);

  const merchantSelectValue = form.merchantId && merchantOptions.some((o) => o.id === form.merchantId)
    ? form.merchantId
    : '';

  const linkedMerchant = useMemo(
    () => merchants.find((m) => m._id === form.merchantId) || null,
    [merchants, form.merchantId],
  );

  const linkedMerchantLabel = useMemo(() => {
    if (!form.merchantId) return '';
    if (linkedMerchant) {
      return `${linkedMerchant.firstName} ${linkedMerchant.lastName || ''}`.trim()
        || linkedMerchant.email
        || linkedMerchant.phone
        || form.merchantId;
    }
    return `Merchant (${form.merchantId.slice(-6)})`;
  }, [form.merchantId, linkedMerchant]);

  const { data: merchantPlans = [], isLoading: merchantPlansLoading, isFetched: merchantPlansFetched } = useQuery({
    queryKey: ['merchant-subscription-plans', form.zoneCode],
    queryFn: () => listPartnerSubscriptionPlans({ partnerType: 'merchant', active: true }),
    enabled: open && form.revenueModel === 'subscription',
    staleTime: 60_000,
  });

  const merchantPlansReady = form.revenueModel !== 'subscription' || !open || merchantPlansFetched;

  const subscriptionPlanOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: { id: string; label: string }[] = [];
    const push = (id: string, label: string) => {
      if (!id || seen.has(id)) return;
      seen.add(id);
      out.push({ id, label });
    };
    if (form.partnerSubscriptionPlanId) {
      const linked = merchantPlans.find((p) => p._id === form.partnerSubscriptionPlanId);
      push(
        form.partnerSubscriptionPlanId,
        linked
          ? `${linked.name} · ${formatMoney(linked.price)}/${linked.billingInterval} · ${linked.zoneCode}`
          : `Current plan (${form.partnerSubscriptionPlanId.slice(-6)})`,
      );
    }
    for (const p of merchantPlans) {
      push(p._id, `${p.name} · ${formatMoney(p.price)}/${p.billingInterval} · ${p.zoneCode}`);
    }
    return out;
  }, [form.partnerSubscriptionPlanId, merchantPlans]);

  const subscriptionPlanValue = merchantPlansReady
    && subscriptionPlanOptions.some((o) => o.id === form.partnerSubscriptionPlanId)
    ? form.partnerSubscriptionPlanId
    : '';

  const merchantMissingFromList = !!form.merchantId
    && merchantsReady
    && !merchants.some((m) => m._id === form.merchantId);

  const subscriptionPlanMissingFromList = !!form.partnerSubscriptionPlanId
    && merchantPlansReady
    && !merchantPlans.some((p) => p._id === form.partnerSubscriptionPlanId);

  const { data: merchantComplianceData } = useQuery({
    queryKey: ['merchant-compliance', form.merchantId],
    queryFn: () => getPartnerDocuments(form.merchantId, 'merchant'),
    enabled: open && !!form.merchantId,
  });

  const merchantCompliance = useMemo(
    () => (merchantComplianceData ? extractMerchantCompliance(merchantComplianceData) : null),
    [merchantComplianceData],
  );

  const documentAvailability = useMemo(
    () => (merchantComplianceData ? getMerchantDocumentAvailability(merchantComplianceData) : null),
    [merchantComplianceData],
  );

  const approvedComplianceSyncPreview = useMemo(() => {
    if (!merchantComplianceData || !documentAvailability?.hasApprovedGstOrFssai) return [];
    const approved = extractApprovedDocumentCompliance(merchantComplianceData);
    return buildApprovedComplianceUpdates(form, approved);
  }, [merchantComplianceData, documentAvailability?.hasApprovedGstOrFssai, form.gstNumber, form.fssaiNumber, form.tradeLicenseNumber]);

  const complianceApplyPreview = useMemo(
    () => approvedComplianceSyncPreview.filter((row) => row.changed),
    [approvedComplianceSyncPreview],
  );

  const requestApplyApprovedDocuments = () => {
    if (!merchantComplianceData || !documentAvailability?.hasApprovedGstOrFssai) {
      setComplianceApplyNotice({
        severity: 'warning',
        message: 'No approved documents available. GST and/or FSSAI must be approved in Document Management before they can be applied to this restaurant.',
      });
      return;
    }
    if (!complianceApplyPreview.length) {
      setComplianceApplyNotice({
        severity: 'info',
        message: 'Approved document values already match the current GST / FSSAI / Trade License fields — nothing new to apply.',
      });
      return;
    }
    setApplyComplianceConfirmOpen(true);
  };

  const confirmApplyApprovedDocuments = () => {
    const changed = merchantComplianceData
      ? buildChangedComplianceUpdates(form, extractApprovedDocumentCompliance(merchantComplianceData))
      : complianceApplyPreview;
    const patchFields: Partial<RestaurantFormState> = {};
    for (const row of changed) {
      patchFields[row.field] = row.next;
    }
    patch(patchFields);
    setApplyComplianceConfirmOpen(false);
    setComplianceApplyNotice({
      severity: 'success',
      message: `Updated ${changed.map((r) => r.label).join(', ')} from approved merchant documents. Other restaurant fields were not changed.`,
    });
    setTab(1);
  };

  const documentSlots = useMemo(() => {
    if (!documentAvailability) return [];
    return [
      { label: 'GST', slot: documentAvailability.gstSlot },
      { label: 'FSSAI', slot: documentAvailability.fssaiSlot },
      { label: 'Trade License', slot: documentAvailability.tradeLicenseSlot },
      { label: 'Owner ID', slot: documentAvailability.ownerIdSlot },
    ] as { label: string; slot: MerchantDocumentSlot }[];
  }, [documentAvailability]);

  const applyMerchantCompliance = (includeLocation = false) => {
    if (!merchantCompliance || !hasMerchantComplianceNumbers(merchantCompliance)) {
      setComplianceApplyNotice({
        severity: 'warning',
        message: 'No GST, FSSAI, or Trade License numbers are available to apply. Approve documents with document numbers first, or enter values manually.',
      });
      return;
    }
    patch({
      gstNumber: merchantCompliance.gstNumber || form.gstNumber,
      fssaiNumber: merchantCompliance.fssaiNumber || form.fssaiNumber,
      tradeLicenseNumber: merchantCompliance.tradeLicenseNumber || form.tradeLicenseNumber,
      ownerName: merchantCompliance.ownerName || form.ownerName,
      addressLine: merchantCompliance.address || form.addressLine,
      city: merchantCompliance.city || form.city,
      ...(includeLocation && merchantCompliance.latitude ? { latitude: merchantCompliance.latitude } : {}),
      ...(includeLocation && merchantCompliance.longitude ? { longitude: merchantCompliance.longitude } : {}),
    });
    setComplianceApplyNotice({
      severity: 'success',
      message: 'Applied available compliance numbers from merchant documents / onboarding.',
    });
  };

  const canApplyComplianceNumbers = Boolean(
    merchantCompliance && hasMerchantComplianceNumbers(merchantCompliance),
  );
  const approvedDocNumbersAvailable = Boolean(documentAvailability?.hasApprovedGstOrFssai
    || documentAvailability?.approvedTradeLicenseNumber);

  const slug = normalizeRestaurantSlug(form.slug || form.name);

  const duplicateErrors = useMemo(
    () => findRestaurantDuplicateErrors(existingRestaurants, form, editing?._id as string | undefined),
    [existingRestaurants, form.name, form.slug, editing?._id],
  );

  const branchOptions = useMemo(() => {
    const rows = masters?.restaurant_branches || [];
    if (!slug) return rows;
    return rows.filter((b) => String(b.metadata?.restaurantSlug || '') === slug);
  }, [masters?.restaurant_branches, slug]);

  const restaurantTypeOptions = useMemo(() => {
    const all = masters?.restaurant_types || [];
    const selectable = all.filter(isMasterRecordSelectable);
    if (form.restaurantTypeCode && !selectable.some((t) => t.code === form.restaurantTypeCode)) {
      const legacy = all.find((t) => t.code === form.restaurantTypeCode);
      if (legacy) return [...selectable, legacy];
    }
    return selectable;
  }, [masters?.restaurant_types, form.restaurantTypeCode]);

  useEffect(() => {
    if (open) {
      setTab(0);
      setTouched({});
      setValidationAttempted(false);
      setComplianceApplyNotice(null);
      setApplyComplianceConfirmOpen(false);
      setMerchantDocumentsOpen(false);
    }
  }, [open, editing?._id]);

  useEffect(() => {
    if (serverFieldErrors.name || serverFieldErrors.slug) {
      setTab(0);
      setTouched((t) => ({ ...t, name: true, slug: true }));
    }
  }, [serverFieldErrors.name, serverFieldErrors.slug]);

  // Ensure Business Type is always a valid master code (create + edit).
  useEffect(() => {
    if (!open || mastersLoading) return;
    const allTypes = masters?.restaurant_types || [];
    const selectableTypes = allTypes.filter(isMasterRecordSelectable);
    if (!allTypes.length) return;

    const current = allTypes.find((t) => t.code === form.restaurantTypeCode);
    if (current) {
      if (!editing && !isMasterRecordSelectable(current) && selectableTypes[0]) {
        patch({ restaurantTypeCode: selectableTypes[0].code });
      }
      return;
    }

    const fromExisting = allTypes.find(
      (t) => t.name.toLowerCase() === String(form.restaurantTypeCode || '').toLowerCase(),
    );
    patch({ restaurantTypeCode: fromExisting?.code || selectableTypes[0]?.code || allTypes[0].code });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?._id, mastersLoading, masters?.restaurant_types]);

  // Map legacy cuisine display names → master codes so chips + validation work on edit.
  useEffect(() => {
    if (!open || mastersLoading) return;
    const cuisines = masters?.cuisine_types || [];
    if (!cuisines.length || !form.cuisineTypeCodes.length) return;
    const codeSet = new Set(cuisines.map((c) => c.code));
    const nameToCode = new Map(cuisines.map((c) => [c.name.toLowerCase(), c.code]));
    const mapped = [...new Set(
      form.cuisineTypeCodes
        .map((raw) => (codeSet.has(raw) ? raw : nameToCode.get(raw.toLowerCase()) || ''))
        .filter(Boolean),
    )];
    const same =
      mapped.length === form.cuisineTypeCodes.length &&
      mapped.every((c, i) => c === form.cuisineTypeCodes[i]);
    if (!same && mapped.length > 0) {
      patch({ cuisineTypeCodes: mapped });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?._id, mastersLoading, masters?.cuisine_types]);

  const toggleCuisine = (code: string) => {
    if (form.cuisineTypeCodes.includes(code)) {
      // Keep remaining order; if primary removed, next becomes primary
      patch({ cuisineTypeCodes: form.cuisineTypeCodes.filter((c) => c !== code) });
      return;
    }
    // First selected cuisine is primary; later selections append as secondary
    patch({ cuisineTypeCodes: [...form.cuisineTypeCodes, code] });
  };

  const setPrimaryCuisine = (code: string) => {
    if (!form.cuisineTypeCodes.includes(code)) return;
    patch({
      cuisineTypeCodes: [code, ...form.cuisineTypeCodes.filter((c) => c !== code)],
    });
  };

  const primaryCuisineCode = form.cuisineTypeCodes[0] || '';

  const updateHour = (index: number, partial: Partial<OpeningHourRow>) => {
    const openingHours = [...form.openingHours];
    openingHours[index] = { ...openingHours[index], ...partial };
    patch({ openingHours });
  };

  const documentsUrl = form.merchantId
    ? `/partners?entityType=merchant&partnerId=${form.merchantId}&reviewDocuments=1`
    : '/partners?entityType=merchant';

  const missingCuisines = !form.cuisineTypeCodes.length;
  const missingType = !form.restaurantTypeCode;
  const missingSubPlan = form.revenueModel === 'subscription' && !form.partnerSubscriptionPlanId;

  const phoneValidation = useMemo(() => {
    const phoneLocal = localPhoneDigits(form.phone);
    const supportLocal = localPhoneDigits(form.supportNumber);
    let phone: string | undefined;
    let supportPhone: string | undefined;

    if (phoneLocal) {
      if (!form.phoneCountryCode.trim()) {
        phone = 'Select a country code before entering the phone number';
      } else {
        phone = validateLocalPhoneForCountry(
          phoneLocal,
          phoneLengthForDialCode(form.phoneCountryCode),
          'Registered phone number',
          { dialCode: form.phoneCountryCode },
        );
      }
    }

    if (supportLocal) {
      if (!form.supportPhoneCountryCode.trim()) {
        supportPhone = 'Select a country code before entering the support number';
      } else {
        supportPhone = validateLocalPhoneForCountry(
          supportLocal,
          phoneLengthForDialCode(form.supportPhoneCountryCode),
          'Support number',
          { dialCode: form.supportPhoneCountryCode },
        );
      }
    }

    return { phone, supportPhone, phoneLocal, supportLocal };
  }, [form.phone, form.phoneCountryCode, form.supportNumber, form.supportPhoneCountryCode]);

  const emailValidation = useMemo(() => {
    const trimmed = form.email.trim();
    if (!trimmed) return { error: undefined as string | undefined, isValid: false };
    const error = validateEmail(trimmed, 'Registered email');
    return { error, isValid: !error };
  }, [form.email]);

  const nameError = validateRestaurantName(form.name)
    || duplicateErrors.name
    || serverFieldErrors.name;
  const slugError = !editing
    ? (duplicateErrors.slug || serverFieldErrors.slug)
    : serverFieldErrors.slug;
  const showNameError = Boolean(nameError && (validationAttempted || touched.name || form.name.length > 0 || serverFieldErrors.name));
  const showSlugError = Boolean(slugError && (validationAttempted || touched.slug || form.slug.length > 0 || serverFieldErrors.slug));
  const showPhoneError = Boolean(
    phoneValidation.phone && (validationAttempted || touched.phone || phoneValidation.phoneLocal.length > 0),
  );
  const showSupportPhoneError = Boolean(
    phoneValidation.supportPhone && (validationAttempted || touched.supportPhone || phoneValidation.supportLocal.length > 0),
  );
  const showEmailError = Boolean(
    emailValidation.error && (validationAttempted || touched.email || form.email.length > 0),
  );
  const showBusinessTypeError = validationAttempted && missingType;
  const showCuisineError = validationAttempted && missingCuisines;
  const missingMerchant = !form.merchantId.trim();
  const missingZone = !form.zoneCode.trim();
  const hasPinnedLocation = (() => {
    const lat = Number(form.latitude);
    const lng = Number(form.longitude);
    return Boolean(form.latitude.trim() && form.longitude.trim() && Number.isFinite(lat) && Number.isFinite(lng));
  })();
  const showMerchantError = validationAttempted && missingMerchant;
  const showZoneError = validationAttempted && missingZone;
  const showLocationError = validationAttempted && !hasPinnedLocation;
  const phoneWelcomeMessage = form.sendWelcomeEmail && !editing
    ? welcomeEmailHelper('phone', welcomeConfig)
    : 'Valid registered number.';
  const emailWelcomeMessage = form.sendWelcomeEmail && !editing
    ? welcomeEmailHelper('email', welcomeConfig)
    : 'Valid registered email.';
  const welcomeConfigReady = welcomeConfig?.ready ?? false;
  const showWelcomeSetupAlert = !editing && form.sendWelcomeEmail && welcomeConfig && !welcomeConfigReady;

  const openingHourErrors = useMemo(
    () => validateRestaurantOpeningHours(form.openingHours),
    [form.openingHours],
  );
  const openingHoursInvalid = hasRestaurantOpeningHourErrors(openingHourErrors);
  const openingHoursSummary = openingHoursValidationSummary(form.openingHours, openingHourErrors);

  const commissionError = form.revenueModel === 'commission'
    ? validateCommissionValue(form.vendorCommissionType, form.vendorCommissionValue)
    : undefined;
  const commissionOutOfRange = Boolean(
    commissionError
    && Number.isFinite(form.vendorCommissionValue)
    && (form.vendorCommissionValue < 0
      || (form.vendorCommissionType === 'percentage' && form.vendorCommissionValue > 100)),
  );
  const showCommissionError = Boolean(
    commissionError && (validationAttempted || touched.commission || commissionOutOfRange),
  );

  const validationIssues = useMemo(
    () => validateRestaurantForm({
      form,
      duplicateErrors,
      serverFieldErrors,
      phoneError: phoneValidation.phone,
      supportPhoneError: phoneValidation.supportPhone,
      emailError: emailValidation.error,
      editing: !!editing,
      complianceRequired,
      documentAvailability,
    }),
    [
      form,
      duplicateErrors,
      serverFieldErrors,
      phoneValidation.phone,
      phoneValidation.supportPhone,
      emailValidation.error,
      editing,
      complianceRequired,
      documentAvailability,
    ],
  );
  const tabErrors = useMemo(() => tabErrorCounts(validationIssues), [validationIssues]);

  const approvalStatusMeta = VERIFICATION_STATUS_OPTIONS.find((o) => o.value === form.verificationStatus);

  const canSubmit = validationIssues.length === 0;

  const revealValidation = () => {
    setValidationAttempted(true);
    setTouched((prev) => {
      const openingHours: Record<number, boolean> = { ...(prev.openingHours || {}) };
      form.openingHours.forEach((_, index) => {
        openingHours[index] = true;
      });
      return {
        ...prev,
        name: true,
        slug: true,
        phone: true,
        supportPhone: true,
        email: true,
        commission: true,
        openingHours,
      };
    });
    if (validationIssues.length > 0) {
      setTab(validationIssues[0].tabIndex);
      requestAnimationFrame(() => {
        document.getElementById('restaurant-form-validation-summary')?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      });
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) {
      revealValidation();
      return;
    }
    onSubmit();
  };

  const goToIssue = (issue: { tabIndex: number }) => {
    setValidationAttempted(true);
    setTab(issue.tabIndex);
  };

  return (
    <FormDrawer
      open={open}
      title={editing ? 'Edit Restaurant Master' : 'Add Restaurant Master'}
      subtitle="Swiggy-style restaurant profile — basic, business, compliance, timing, approval, and revenue model."
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={editing ? 'Update' : 'Create'}
      loading={loading}
      disableSubmit={loading}
      submitHint={
        validationAttempted && !canSubmit
          ? `Create blocked — fix ${validationIssues.length} item${validationIssues.length === 1 ? '' : 's'} listed above (click each to jump to the tab).`
          : undefined
      }
      width={720}
    >
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>
      )}
      {validationAttempted && !canSubmit && (
        <Alert
          severity="error"
          id="restaurant-form-validation-summary"
          sx={{
            mb: 2,
            bgcolor: 'error.main',
            color: 'error.contrastText',
            '& .MuiAlert-icon': { color: 'error.contrastText' },
            '& .MuiAlert-message': { width: '100%', color: 'error.contrastText' },
          }}
        >
          <Typography variant="subtitle2" fontWeight={800} gutterBottom sx={{ color: 'inherit' }}>
            {validationIssues.length} required field{validationIssues.length === 1 ? '' : 's'} need attention before {editing ? 'update' : 'create'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: 'inherit', opacity: 0.95 }}>
            Click a field below to open its tab. Fix each item, then click {editing ? 'Update' : 'Create'} again.
          </Typography>
          <Stack spacing={0.75} component="ul" sx={{ m: 0, pl: 2.5 }}>
            {validationIssues.map((issue) => (
              <Box component="li" key={`${issue.tabId}-${issue.field}`}>
                <Button
                  size="small"
                  onClick={() => goToIssue(issue)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    color: 'inherit',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    px: 0,
                    py: 0.25,
                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  [{RESTAURANT_MASTER_TABS[issue.tabIndex].label}] {issue.field} — {issue.message}
                </Button>
              </Box>
            ))}
          </Stack>
        </Alert>
      )}
      {!mastersLoading && !(masters?.restaurant_types?.length) && (
        <Alert severity="warning" sx={{ mb: 2, '& .MuiAlert-message': { color: 'text.primary', fontWeight: 600 } }}>
          Seed food masters first: Food Delivery → Masters (restaurant types, cuisines, opening hours).
        </Alert>
      )}
      {showWelcomeSetupAlert && (
        <Alert severity="info" sx={{ mb: 2, '& .MuiAlert-message': { color: 'text.primary' } }}>
          Welcome email is not fully configured yet.
          {!welcomeConfig?.smtpConfigured && ' Set SMTP_HOST, SMTP_USER, SMTP_PASS, and EMAIL_FROM in backend `.env`.'}
          {!welcomeConfig?.supportEmail && !welcomeEmailConfig.supportEmail && ' Set SUPPORT_EMAIL in backend `.env` and VITE_SUPPORT_EMAIL in frontend `.env`.'}
          {!welcomeConfig?.enabled && ' Set WELCOME_EMAIL_ENABLED=true in backend `.env`.'}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {RESTAURANT_MASTER_TABS.map((t, index) => {
          const errorCount = validationAttempted ? (tabErrors[index] || 0) : 0;
          return (
            <Tab
              key={t.id}
              label={(
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <span>{t.label}</span>
                  {errorCount > 0 && (
                    <Box
                      component="span"
                      sx={{
                        minWidth: 18,
                        height: 18,
                        px: 0.5,
                        borderRadius: 9,
                        bgcolor: 'error.main',
                        color: 'error.contrastText',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        lineHeight: '18px',
                        textAlign: 'center',
                      }}
                    >
                      {errorCount}
                    </Box>
                  )}
                </Stack>
              )}
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }}
            />
          );
        })}
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={700}>Basic Details</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Identity, contact, location, and branding. Fields marked with * are required before create.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <PersonNameField
              fullWidth
              required
              label="Restaurant Name"
              value={form.name}
              error={showNameError}
              helperText={
                showNameError
                  ? nameError
                  : 'Letters and spaces only — no numbers or special characters'
              }
              inputProps={{ maxLength: 80 }}
              onBlur={() => setTouched((t) => ({ ...t, name: true }))}
              onChange={(e) => patch({
                name: e.target.value,
                slug: form.slug ? form.slug : normalizeRestaurantSlug(e.target.value),
              })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <PersonNameField fullWidth label="Owner Name" value={form.ownerName}
              onChange={(e) => patch({ ownerName: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required={!editing}
              label="Slug"
              value={form.slug}
              disabled={!!editing}
              error={showSlugError}
              helperText={
                showSlugError
                  ? slugError
                  : editing
                    ? `Locked after create — branch key: ${slug || '—'}`
                    : `Unique URL key for branches — auto-filled from name: ${slug || '—'}`
              }
              onBlur={() => setTouched((t) => ({ ...t, slug: true }))}
              onChange={(e) => patch({ slug: normalizeRestaurantSlug(e.target.value) })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth required label="Merchant Account" value={merchantSelectValue}
              disabled={!merchantsReady}
              error={showMerchantError}
              onChange={(e) => patch({ merchantId: e.target.value })}
              helperText={
                showMerchantError
                  ? 'Required — select the merchant that owns this restaurant'
                  : !merchantsReady
                    ? 'Loading merchants…'
                    : merchantMissingFromList
                      ? 'Linked merchant not in current list — still saved on this restaurant'
                      : 'Link to merchant for onboarding documents & approval'
              }>
              <MenuItem value="">— Select merchant —</MenuItem>
              {merchantOptions.map((m) => (
                <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box>
              <PhoneWithDialCodeField
                label="Registered Phone No"
                layout="inline"
                phone={form.phone}
                countryCode={form.phoneCountryCode}
                preferredCountry="IN"
                hideHelper={Boolean(phoneValidation.phoneLocal && !phoneValidation.phone)}
                onChange={({ phone, countryCode }) => patch({ phone, phoneCountryCode: countryCode })}
                onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                error={showPhoneError}
                helperText={
                  showPhoneError
                    ? phoneValidation.phone
                    : 'Select country code, then enter the registered mobile number'
                }
              />
              {phoneValidation.phoneLocal && !phoneValidation.phone && (
                <FormHelperText sx={{ color: 'success.main', mx: '14px', mt: 0 }}>
                  {phoneWelcomeMessage}
                </FormHelperText>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="email"
              label="Registered Email"
              value={form.email}
              error={showEmailError}
              helperText={
                emailValidation.isValid
                  ? emailWelcomeMessage
                  : showEmailError
                    ? emailValidation.error
                    : 'Restaurant contact email for onboarding and account notices'
              }
              FormHelperTextProps={
                emailValidation.isValid ? { sx: { color: 'success.main' } } : undefined
              }
              onBlur={() => setTouched((t) => ({ ...t, email: true }))}
              onChange={(e) => patch({ email: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <PhoneWithDialCodeField
              label="Support Number"
              layout="inline"
              phone={form.supportNumber}
              countryCode={form.supportPhoneCountryCode}
              preferredCountry="IN"
              onChange={({ phone, countryCode }) => patch({ supportNumber: phone, supportPhoneCountryCode: countryCode })}
              onBlur={() => setTouched((t) => ({ ...t, supportPhone: true }))}
              error={showSupportPhoneError}
              helperText={
                showSupportPhoneError
                  ? phoneValidation.supportPhone
                  : 'Customer support line shown in apps and admin lists'
              }
            />
          </Grid>
          {!editing && (
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={form.sendWelcomeEmail}
                    onChange={(e) => patch({ sendWelcomeEmail: e.target.checked })}
                  />
                )}
                label="Send welcome message (email and/or SMS when configured)"
              />
              {welcomeConfig?.supportEmail || welcomeEmailConfig.supportEmail ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5, mt: -0.5 }}>
                  Support / reply-to: {welcomeConfig?.supportEmail || welcomeEmailConfig.supportEmail}
                </Typography>
              ) : null}
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={10}
              label="Notes / Description"
              placeholder="e.g. Pure veg kitchen, famous for biryani, open till midnight, delivery within 5 km…"
              value={form.description}
              inputProps={{ maxLength: 500 }}
              helperText={`${form.description.length}/500 characters — shown on the restaurant profile in customer apps and admin lists`}
              onBlur={() => setTouched((t) => ({ ...t, description: true }))}
              onChange={(e) => patch({ description: e.target.value })}
              sx={{
                '& .MuiInputBase-inputMultiline': {
                  fontSize: '0.95rem',
                  lineHeight: 1.65,
                  color: 'text.primary',
                },
                '& .MuiInputBase-root': {
                  bgcolor: alpha('#0f172a', 0.02),
                },
              }}
            />
            {form.description.trim() ? (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.75,
                  borderRadius: 1.5,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.75, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Profile preview
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.65, color: 'text.primary' }}>
                  {form.description}
                </Typography>
              </Box>
            ) : (
              <FormHelperText sx={{ mt: 1, mx: 0 }}>
                Notes appear on the restaurant profile — enter text above to see a live preview.
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Address" value={form.addressLine}
              onChange={(e) => patch({ addressLine: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="City" value={form.city} onChange={(e) => patch({ city: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="State" value={form.state} onChange={(e) => patch({ state: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Pincode" value={form.pincode} onChange={(e) => patch({ pincode: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <RestaurantLocationPicker
              latitude={form.latitude}
              longitude={form.longitude}
              addressLine={form.addressLine}
              city={form.city}
              required
              error={showLocationError}
              helperText={
                showLocationError
                  ? 'Required — search an address or click the map to pin the restaurant'
                  : undefined
              }
              onChange={(latitude, longitude) => patch({ latitude, longitude })}
              onAddressSuggest={(parts) => patch({
                addressLine: parts.addressLine || form.addressLine,
                city: parts.city || form.city,
                state: parts.state || form.state,
                pincode: parts.pincode || form.pincode,
              })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ZoneMasterSelect
              label="Zone"
              value={form.zoneCode}
              required
              error={showZoneError}
              helperText={
                showZoneError
                  ? 'Required — select the operational delivery zone'
                  : 'Delivery/dispatch zone for this restaurant'
              }
              onChange={(code) => patch({ zoneCode: code })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormFileUpload
              label="Logo (optional)"
              value={form.logo}
              onChange={(url) => patch({ logo: url })}
              folder="food/restaurants/logo"
              helperText="Optional square logo for lists and profile"
              imageConstraints={{ maxWidth: 2048, maxHeight: 2048, minWidth: 64, minHeight: 64 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormFileUpload
              label="Cover Image (optional)"
              value={form.coverImage}
              onChange={(url) => patch({ coverImage: url })}
              folder="food/restaurants/cover"
              helperText="Optional wide banner for the restaurant profile"
              imageConstraints={{ maxWidth: 4096, maxHeight: 4096, minWidth: 200, minHeight: 120 }}
            />
          </Grid>
        </Grid>
      )}

      {tab === 1 && (
        <Grid container spacing={2}>
          {form.merchantId && documentAvailability && (
            <Grid item xs={12}>
              {canApplyComplianceNumbers ? (
                <Alert
                  severity={approvedDocNumbersAvailable ? 'success' : 'info'}
                  sx={{ '& .MuiAlert-message': { color: 'text.primary', width: '100%' } }}
                  action={(
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        color="inherit"
                        variant="outlined"
                        onClick={() => applyMerchantCompliance(false)}
                      >
                        Apply compliance
                      </Button>
                      {(merchantCompliance?.latitude || merchantCompliance?.longitude) && (
                        <Button
                          size="small"
                          color="inherit"
                          variant="outlined"
                          onClick={() => applyMerchantCompliance(true)}
                        >
                          + location
                        </Button>
                      )}
                    </Stack>
                  )}
                >
                  <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                    {approvedDocNumbersAvailable
                      ? 'Approved merchant document numbers available'
                      : 'Compliance numbers found in merchant onboarding (documents may still need approval)'}
                  </Typography>
                  <Stack spacing={0.35}>
                    <Typography variant="body2">
                      GST: <strong>{merchantCompliance?.gstNumber || '—'}</strong>
                      {' · '}Status: {formatMerchantDocumentStatus(documentAvailability.gst)}
                      {documentAvailability.gstSlot.documentNumber && !documentAvailability.gstSlot.approved
                        ? ` · on file: ${documentAvailability.gstSlot.documentNumber}`
                        : ''}
                    </Typography>
                    <Typography variant="body2">
                      FSSAI: <strong>{merchantCompliance?.fssaiNumber || '—'}</strong>
                      {' · '}Status: {formatMerchantDocumentStatus(documentAvailability.fssai)}
                      {documentAvailability.fssaiSlot.documentNumber && !documentAvailability.fssaiSlot.approved
                        ? ` · on file: ${documentAvailability.fssaiSlot.documentNumber}`
                        : ''}
                    </Typography>
                    <Typography variant="body2">
                      Trade License: <strong>{merchantCompliance?.tradeLicenseNumber || '—'}</strong>
                      {' · '}Status: {formatMerchantDocumentStatus(documentAvailability.tradeLicense)}
                    </Typography>
                  </Stack>
                  {merchantCompliance?.sources.length ? (
                    <Typography variant="caption" display="block" sx={{ mt: 0.75 }}>
                      Sources: {merchantCompliance.sources.join(', ')}
                    </Typography>
                  ) : null}
                </Alert>
              ) : (
                <Alert
                  severity="warning"
                  sx={{ '& .MuiAlert-message': { color: 'text.primary', fontWeight: 600 } }}
                >
                  <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                    No GST / FSSAI / Trade License numbers available to apply
                  </Typography>
                  <Stack spacing={0.35} sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      GST: {formatMerchantDocumentStatus(documentAvailability.gst)}
                      {documentAvailability.gstSlot.documentNumber
                        ? ` · ${documentAvailability.gstSlot.documentNumber}`
                        : ' · no number'}
                    </Typography>
                    <Typography variant="body2">
                      FSSAI: {formatMerchantDocumentStatus(documentAvailability.fssai)}
                      {documentAvailability.fssaiSlot.documentNumber
                        ? ` · ${documentAvailability.fssaiSlot.documentNumber}`
                        : ' · no number'}
                    </Typography>
                    <Typography variant="body2">
                      Trade License: {formatMerchantDocumentStatus(documentAvailability.tradeLicense)}
                      {documentAvailability.tradeLicenseSlot.documentNumber
                        ? ` · ${documentAvailability.tradeLicenseSlot.documentNumber}`
                        : ' · no number'}
                    </Typography>
                  </Stack>
                  <Typography variant="body2">
                    Upload and approve merchant documents with document numbers, or enter values manually below.
                    Apply compliance stays disabled until numbers are available.
                  </Typography>
                </Alert>
              )}
            </Grid>
          )}
          {form.merchantId && !merchantComplianceData && (
            <Grid item xs={12}>
              <Alert severity="info">Loading merchant document status…</Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={700}>Business & Compliance</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Fields marked with * follow Merchant / Vendor Document rules and are required before create.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth required label="Business Type"
              value={mastersLoading ? '' : (form.restaurantTypeCode || '')}
              disabled={mastersLoading || !restaurantTypeOptions.length}
              error={showBusinessTypeError}
              helperText={
                showBusinessTypeError
                  ? 'Required — select a restaurant business type'
                  : 'Only active, non-deprecated types are available for new restaurants'
              }
              onChange={(e) => patch({ restaurantTypeCode: e.target.value })}>
              <MenuItem value="">— Select —</MenuItem>
              {restaurantTypeOptions.map((t) => (
                <MenuItem key={t.code} value={t.code} disabled={!isMasterRecordSelectable(t)}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {masterImageUrl(t) && <Avatar src={resolveAssetUrl(masterImageUrl(t))} sx={{ width: 24, height: 24 }} />}
                    <span>
                      {t.name}
                      {!isMasterRecordSelectable(t) ? ' (deprecated)' : ''}
                    </span>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Branch (optional)" value={form.branchCode}
              onChange={(e) => patch({ branchCode: e.target.value })}>
              <MenuItem value="">— None —</MenuItem>
              {branchOptions.map((b) => (
                <MenuItem key={b.code} value={b.code}>{b.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              gutterBottom
              color={showCuisineError ? 'error' : 'text.primary'}
              component="label"
            >
              Cuisines
              <Box component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>*</Box>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Select one or more cuisines. The first selected is the <strong>primary</strong> cuisine shown in apps.
              Use ★ on a selected chip to make it primary.
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {(masters?.cuisine_types || []).map((c) => {
                const selected = form.cuisineTypeCodes.includes(c.code);
                const isPrimary = primaryCuisineCode === c.code;
                const img = masterImageUrl(c);
                return (
                  <Chip
                    key={c.code}
                    label={isPrimary ? `${c.name} · Primary` : c.name}
                    avatar={img ? <Avatar src={resolveAssetUrl(img)} /> : undefined}
                    color={selected ? (isPrimary ? 'secondary' : 'primary') : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => toggleCuisine(c.code)}
                    onDelete={selected && !isPrimary ? () => setPrimaryCuisine(c.code) : undefined}
                    deleteIcon={selected && !isPrimary ? (
                      <Box component="span" sx={{ px: 0.5, fontSize: '0.65rem', fontWeight: 800 }}>★</Box>
                    ) : undefined}
                    title={selected && !isPrimary ? 'Make primary cuisine' : undefined}
                    sx={showCuisineError && !form.cuisineTypeCodes.length
                      ? { borderColor: 'error.main' }
                      : undefined}
                  />
                );
              })}
            </Stack>
            {primaryCuisineCode && (
              <FormHelperText sx={{ mt: 0.75, mx: 0 }}>
                Primary cuisine: {(masters?.cuisine_types || []).find((c) => c.code === primaryCuisineCode)?.name || primaryCuisineCode}
                {form.cuisineTypeCodes.length > 1
                  ? ` · ${form.cuisineTypeCodes.length - 1} additional cuisine${form.cuisineTypeCodes.length === 2 ? '' : 's'}`
                  : ''}
              </FormHelperText>
            )}
            {showCuisineError && (
              <FormHelperText error sx={{ mt: 0.75, mx: 0 }}>
                Select at least one cuisine — the first becomes primary
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required={complianceRequired.gstNumber}
              label={complianceRequired.gstNumber ? 'GST Number' : 'GST Number (optional)'}
              value={form.gstNumber}
              error={validationAttempted && complianceRequired.gstNumber && !form.gstNumber.trim()}
              helperText={
                validationAttempted && complianceRequired.gstNumber && !form.gstNumber.trim()
                  ? 'Required by Merchant Document rules'
                  : complianceRequired.gstNumber
                    ? 'Required — matches Merchant / Vendor Document master'
                    : 'Optional unless required in Merchant Document masters'
              }
              onChange={(e) => patch({ gstNumber: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required={complianceRequired.fssaiNumber}
              label={complianceRequired.fssaiNumber ? 'FSSAI Number' : 'FSSAI Number (optional)'}
              value={form.fssaiNumber}
              error={validationAttempted && complianceRequired.fssaiNumber && !form.fssaiNumber.trim()}
              helperText={
                validationAttempted && complianceRequired.fssaiNumber && !form.fssaiNumber.trim()
                  ? 'Required by Merchant Document rules'
                  : complianceRequired.fssaiNumber
                    ? 'Required — matches Merchant / Vendor Document master'
                    : 'Optional unless required in Merchant Document masters'
              }
              onChange={(e) => patch({ fssaiNumber: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required={complianceRequired.tradeLicenseNumber}
              label={complianceRequired.tradeLicenseNumber ? 'Trade License' : 'Trade License (optional)'}
              value={form.tradeLicenseNumber}
              error={validationAttempted && complianceRequired.tradeLicenseNumber && !form.tradeLicenseNumber.trim()}
              helperText={
                validationAttempted && complianceRequired.tradeLicenseNumber && !form.tradeLicenseNumber.trim()
                  ? 'Required by Merchant Document rules'
                  : complianceRequired.tradeLicenseNumber
                    ? 'Required — matches Merchant / Vendor Document master'
                    : 'Optional unless required in Merchant Document masters'
              }
              onChange={(e) => patch({ tradeLicenseNumber: e.target.value })}
            />
          </Grid>
        </Grid>
      )}

      {tab === 2 && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={700}>Settings</Typography>
            <Typography variant="body2" color="text.secondary">
              Ordering modes, minimum order, and listing availability. Customers can place orders only when the restaurant is
              <strong> Active</strong>, <strong>Open Now</strong>, and has at least one ordering mode enabled.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Menu filter</Typography>
            <FormControlLabel
              control={<Switch checked={form.isPureVeg} onChange={(e) => patch({ isPureVeg: e.target.checked })} />}
              label="Pure Veg"
            />
            <FormHelperText sx={{ mx: 0, mt: 0 }}>
              When ON, the restaurant is tagged Pure Veg for customer filters and discovery. It does not automatically remove non-veg menu items — keep the menu aligned with this flag.
            </FormHelperText>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Ordering modes
              <Box component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>*</Box>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              At least one mode must stay ON. Delivery controls whether customers can place delivery orders (independent of Open Now / Active Listing).
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 0.5 }}>
              <FormControlLabel
                control={<Switch checked={form.deliveryEnabled} onChange={(e) => patch({ deliveryEnabled: e.target.checked })} />}
                label="Delivery"
              />
              <FormControlLabel
                control={<Switch checked={form.takeawayEnabled} onChange={(e) => patch({ takeawayEnabled: e.target.checked })} />}
                label="Takeaway"
              />
              <FormControlLabel
                control={<Switch checked={form.dineInEnabled} onChange={(e) => patch({ dineInEnabled: e.target.checked })} />}
                label="Dine In"
              />
            </Stack>
            <Stack spacing={0.35} sx={{ mb: 1 }}>
              <FormHelperText sx={{ mx: 0 }}>
                <strong>Delivery:</strong> customers can order for delivery when the restaurant is Active and Open Now.
              </FormHelperText>
              <FormHelperText sx={{ mx: 0 }}>
                <strong>Takeaway / Dine In:</strong> enable pickup or dine-in ordering channels for this restaurant.
              </FormHelperText>
            </Stack>
            {validationAttempted && !(form.deliveryEnabled || form.takeawayEnabled || form.dineInEnabled) && (
              <Alert severity="error" sx={{ mt: 1, '& .MuiAlert-message': { fontWeight: 600, color: 'text.primary' } }}>
                Enable at least one ordering mode: Delivery, Takeaway, or Dine In.
              </Alert>
            )}
            {form.isActive && form.isOpen && (form.deliveryEnabled || form.takeawayEnabled || form.dineInEnabled) && (
              <Alert severity="success" sx={{ mt: 1, '& .MuiAlert-message': { color: 'text.primary' } }}>
                Orderable configuration: Active Listing + Open Now + at least one mode.
              </Alert>
            )}
            {form.isActive && !form.isOpen && (
              <Alert severity="info" sx={{ mt: 1, '& .MuiAlert-message': { color: 'text.primary' } }}>
                Active but Closed: may stay visible in apps (when approved), but customers cannot place orders until Open Now is ON.
              </Alert>
            )}
            {form.isActive && form.isOpen && !(form.deliveryEnabled || form.takeawayEnabled || form.dineInEnabled) && (
              <Alert severity="warning" sx={{ mt: 1, '& .MuiAlert-message': { color: 'text.primary', fontWeight: 600 } }}>
                Active and Open, but no ordering mode is enabled — the restaurant is not orderable.
              </Alert>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="number"
              label={chargeFieldLabel('Min Order')}
              value={form.minOrderAmount}
              error={Boolean(
                validationAttempted
                && validateRestaurantMinOrderAmount(form.minOrderAmount),
              )}
              helperText={
                (validationAttempted && validateRestaurantMinOrderAmount(form.minOrderAmount))
                || `Cart subtotal must be at least this amount before checkout. Allowed: ${getCurrencySymbol()}${RESTAURANT_MIN_ORDER_MIN}–${getCurrencySymbol()}${RESTAURANT_MIN_ORDER_MAX.toLocaleString('en-IN')} (whole rupees; zero/negative not allowed).`
              }
              inputProps={{ min: RESTAURANT_MIN_ORDER_MIN, max: RESTAURANT_MIN_ORDER_MAX, step: 1 }}
              onChange={(e) => patch({ minOrderAmount: Number(e.target.value) })}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Listing & availability</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box flex={1}>
                <FormControlLabel
                  control={<Switch checked={form.isActive} onChange={(e) => patch({ isActive: e.target.checked })} />}
                  label="Active Listing"
                />
                <FormHelperText sx={{ mx: 0 }}>
                  Master listing status. OFF hides the restaurant from customer apps. ON does not by itself make it orderable — Open Now and an ordering mode are also required.
                </FormHelperText>
              </Box>
              <Box flex={1}>
                <FormControlLabel
                  control={<Switch checked={form.isOpen} onChange={(e) => patch({ isOpen: e.target.checked })} />}
                  label="Open Now"
                />
                <FormHelperText sx={{ mx: 0 }}>
                  Temporary operational availability (accepting orders right now). Turn OFF for breaks or holidays without deactivating the listing.
                </FormHelperText>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      )}

      {tab === 3 && (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2">
              Weekly Schedule
              <Box component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>*</Box>
            </Typography>
            <Button size="small" variant="outlined" disabled={!masters?.restaurant_opening_hours?.length}
              onClick={() => {
                const templates = masters?.restaurant_opening_hours || [];
                if (templates.length) patch({ openingHours: openingHoursFromMasters(templates) });
              }}>
              Apply from masters
            </Button>
          </Stack>
          <Alert
            severity={openingHoursInvalid ? 'error' : 'info'}
            sx={{ mb: 2, '& .MuiAlert-message': { color: 'text.primary', fontWeight: openingHoursInvalid ? 600 : 500 } }}
          >
            {openingHoursInvalid
              ? (openingHoursSummary || 'Fix open/close times — closing time must be later than opening time on the same day.')
              : (
                <>
                  Use 24-hour times (e.g. 10:00–22:00). For each open day, both Open and Close are required, and Close must be later than Open.
                  {' '}
                  <strong>Overnight schedules are not supported</strong>
                  {' '}
                  (e.g. 22:00–02:00 is invalid — mark the day Closed or use same-day hours only).
                </>
              )}
          </Alert>
          {form.openingHours.map((row, i) => {
            const dayLabel = WEEK_DAYS.find((d) => d.value === row.day)?.label || row.day;
            const rowErrors = openingHourErrors[i];
            const rowTouched = touched.openingHours?.[i];
            const showRowErrors = Boolean(validationAttempted || rowTouched);
            const showOpenError = Boolean(rowErrors?.openTime && showRowErrors);
            const showCloseError = Boolean(rowErrors?.closeTime && showRowErrors);
            const isOpenDay = !row.isClosed;
            return (
              <Box
                key={row.day}
                sx={{
                  mb: 1.25,
                  p: 1.25,
                  borderRadius: 2,
                  border: 1,
                  borderColor: showOpenError || showCloseError
                    ? 'error.main'
                    : row.isClosed
                      ? 'divider'
                      : 'success.light',
                  bgcolor: row.isClosed ? 'action.hover' : 'background.paper',
                }}
              >
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={2}>
                    <Typography variant="body2" fontWeight={700}>{dayLabel}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={row.isClosed ? 'Closed' : 'Open'}
                        color={row.isClosed ? 'default' : 'success'}
                        variant={row.isClosed ? 'outlined' : 'filled'}
                        sx={{ fontWeight: 700, minWidth: 72 }}
                      />
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={(
                          <Switch
                            size="small"
                            checked={isOpenDay}
                            color={isOpenDay ? 'success' : 'default'}
                            onChange={(e) => updateHour(i, { isClosed: !e.target.checked })}
                            inputProps={{ 'aria-label': `${dayLabel} open or closed` }}
                          />
                        )}
                        label={
                          <Typography variant="caption" fontWeight={700} color={isOpenDay ? 'success.main' : 'text.secondary'}>
                            {isOpenDay ? 'Open this day' : 'Closed all day'}
                          </Typography>
                        }
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="time"
                      label="Open"
                      value={row.openTime}
                      disabled={row.isClosed}
                      required={isOpenDay}
                      error={showOpenError}
                      helperText={
                        row.isClosed
                          ? 'Ignored while Closed'
                          : showOpenError
                            ? rowErrors?.openTime
                            : 'Required when open'
                      }
                      FormHelperTextProps={{ sx: { minHeight: 20, m: 0 } }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      onBlur={() => setTouched((t) => ({
                        ...t,
                        openingHours: { ...(t.openingHours || {}), [i]: true },
                      }))}
                      onChange={(e) => updateHour(i, { openTime: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="time"
                      label="Close"
                      value={row.closeTime}
                      disabled={row.isClosed}
                      required={isOpenDay}
                      error={showCloseError}
                      helperText={
                        row.isClosed
                          ? 'Ignored while Closed'
                          : showCloseError
                            ? rowErrors?.closeTime
                            : 'Must be after Open'
                      }
                      FormHelperTextProps={{ sx: { minHeight: 20, m: 0 } }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      onBlur={() => setTouched((t) => ({
                        ...t,
                        openingHours: { ...(t.openingHours || {}), [i]: true },
                      }))}
                      onChange={(e) => updateHour(i, { closeTime: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </Box>
            );
          })}
          <TextField select fullWidth label="Weekly Holiday" value={form.weeklyHoliday} sx={{ mt: 2 }}
            onChange={(e) => patch({ weeklyHoliday: e.target.value })}
            helperText="Optional full-day off (in addition to per-day Closed switches)">
            <MenuItem value="">— None —</MenuItem>
            {WEEK_DAYS.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
          </TextField>
        </Box>
      )}

      {tab === 4 && (
        <Grid container spacing={2}>
          {!editing && (
            <Grid item xs={12}>
              <Alert severity="warning" icon={false} sx={readableAlertSx}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" fontWeight={700} color="text.primary">Initial workflow:</Typography>
                  <Chip
                    size="small"
                    label="Pending (default)"
                    color="warning"
                    variant={form.verificationStatus === 'pending' ? 'filled' : 'outlined'}
                  />
                  <Typography variant="body2" fontWeight={600} color="text.primary">
                    New restaurants start as Pending until an admin approves them after document and profile review.
                  </Typography>
                </Stack>
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Approval Status"
              value={form.verificationStatus}
              error={validationAttempted && form.verificationStatus === 'approved' && validationIssues.some((i) => i.field === 'Approval Status')}
              helperText={
                validationAttempted && form.verificationStatus === 'approved' && validationIssues.some((i) => i.field === 'Approval Status')
                  ? validationIssues.find((i) => i.field === 'Approval Status')?.message
                  : !editing && form.verificationStatus === 'pending'
                    ? 'Default for create — restaurant stays hidden from customer apps until set to Approved'
                    : form.verificationStatus === 'approved'
                      ? 'Approved requires required compliance documents/numbers first — see Documents tab'
                      : form.verificationStatus === 'rejected'
                        ? 'Rejected — not listed; merchant can fix and resubmit'
                        : form.verificationStatus === 'blocked'
                          ? 'Blocked — listing deactivated automatically'
                          : 'Awaiting admin review'
              }
              onChange={(e) => patch({ verificationStatus: e.target.value as RestaurantFormState['verificationStatus'] })}
            >
              {VERIFICATION_STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={o.label} color={o.color} variant="outlined" />
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {approvalStatusMeta && (
            <Grid item xs={12} sm={6} display="flex" alignItems="center">
              <Chip label={`Current: ${approvalStatusMeta.label}`} color={approvalStatusMeta.color} />
            </Grid>
          )}
          {form.verificationStatus === 'approved' && (
            <Grid item xs={12}>
              <Alert
                severity={
                  validationIssues.some((i) => i.field === 'Approval Status') ? 'error' : 'info'
                }
                sx={readableAlertSx}
              >
                {validationIssues.some((i) => i.field === 'Approval Status')
                  ? (
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" fontWeight={800} color="text.primary">
                        Approval blocked until compliance is complete
                      </Typography>
                      {validationIssues.filter((i) => i.field === 'Approval Status').map((issue) => (
                        <Typography key={issue.message} variant="body2" color="text.primary" fontWeight={600}>
                          • {issue.message}
                        </Typography>
                      ))}
                    </Stack>
                  )
                  : (
                    <Typography variant="body2" color="text.primary" fontWeight={600}>
                      Compliance checks passed for approval. Restaurant becomes visible when also Active and Open.
                    </Typography>
                  )}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              required
              label="Revenue Model"
              value={form.revenueModel}
              FormHelperTextProps={{ sx: { color: 'text.primary', fontWeight: 700 } }}
              helperText={
                form.revenueModel === 'commission'
                  ? 'Commission based requires Commission Type + Commission Value below for order settlement'
                  : 'Subscription based: select a merchant plan — commission fields are hidden'
              }
              onChange={(e) => patch({ revenueModel: e.target.value as RestaurantFormState['revenueModel'] })}
            >
              <MenuItem value="commission">Commission based</MenuItem>
              <MenuItem value="subscription">Subscription based</MenuItem>
            </TextField>
          </Grid>
          {form.revenueModel === 'commission' && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ ...highContrastInfoSx, py: 1 }}>
                <Typography variant="subtitle2" fontWeight={800} gutterBottom sx={{ color: '#082f49' }}>
                  How commission is calculated
                </Typography>
                <Typography variant="body2" sx={{ color: '#0c4a6e', fontWeight: 700, mb: 0.75 }}>
                  Revenue Model = <strong>Commission based</strong> → set <strong>Commission Type</strong> and <strong>Commission Value</strong>.
                  Both are required for settlement on every order.
                </Typography>
                <Stack spacing={0.35}>
                  <Typography variant="body2" sx={{ color: '#0c4a6e', fontWeight: 700 }}>
                    • <strong>Percentage (%)</strong>: platform fee = order value × (value ÷ 100). Allowed range <strong>0–100</strong>.
                    Example: value <strong>15</strong> on a {getCurrencySymbol()}1,000 order → {getCurrencySymbol()}150 commission.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#0c4a6e', fontWeight: 700 }}>
                    • <strong>{chargeFieldLabel('Fixed')}</strong>: platform fee = the value in the zone/tenant currency on every order (0 or greater).
                    Example: value <strong>25</strong> → {getCurrencySymbol()}25 per order.
                  </Typography>
                </Stack>
              </Alert>
            </Grid>
          )}
          {form.revenueModel === 'subscription' && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ ...highContrastInfoSx, py: 1 }}>
                <Typography variant="body2" sx={{ color: '#0c4a6e', fontWeight: 700 }}>
                  <strong>Subscription based:</strong> Commission Type and Commission Value are not used.
                  Select a merchant subscription plan instead (flat fee; commission waived per plan rules).
                </Typography>
              </Alert>
            </Grid>
          )}
          {form.revenueModel === 'commission' && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  required
                  label="Commission Type"
                  value={form.vendorCommissionType}
                  FormHelperTextProps={{ sx: { color: 'text.primary', fontWeight: 700 } }}
                  helperText={
                    form.vendorCommissionType === 'percentage'
                      ? 'Percentage of each order’s value (requires Commission Value 0–100)'
                      : 'Fixed rupee amount charged on each order (requires Commission Value ≥ 0)'
                  }
                  onChange={(e) => patch({ vendorCommissionType: e.target.value as 'percentage' | 'fixed' })}
                >
                  <MenuItem value="percentage">Percentage (%)</MenuItem>
                  <MenuItem value="fixed">{chargeFieldLabel('Fixed')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label={form.vendorCommissionType === 'percentage' ? 'Commission Value (%)' : chargeFieldLabel('Commission Value')}
                  value={form.vendorCommissionValue}
                  error={showCommissionError}
                  helperText={
                    showCommissionError
                      ? commissionError
                      : commissionValueHelperText(form.vendorCommissionType)
                  }
                  FormHelperTextProps={{
                    sx: {
                      color: showCommissionError ? 'error.main' : 'text.primary',
                      fontWeight: 700,
                    },
                  }}
                  inputProps={{
                    min: 0,
                    ...(form.vendorCommissionType === 'percentage' ? { max: 100, step: 0.1 } : { step: 1 }),
                  }}
                  onBlur={() => setTouched((t) => ({ ...t, commission: true }))}
                  onChange={(e) => patch({ vendorCommissionValue: Number(e.target.value) })}
                />
              </Grid>
              {!showCommissionError && Number.isFinite(form.vendorCommissionValue) && form.vendorCommissionValue >= 0 && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: '#0284c7',
                      bgcolor: '#f0f9ff',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#0c4a6e', fontWeight: 800 }}>
                      Live example ({getCurrencySymbol()}1,000 order):{' '}
                      {form.vendorCommissionType === 'percentage'
                        ? `platform commission = ${getCurrencySymbol()}${((Math.min(100, Math.max(0, form.vendorCommissionValue)) / 100) * 1000).toFixed(2)} (${form.vendorCommissionValue}%)`
                        : `platform commission = ${getCurrencySymbol()}${Math.max(0, form.vendorCommissionValue).toFixed(2)} (fixed)`}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </>
          )}
          {form.revenueModel === 'subscription' && (
            <Grid item xs={12}>
              <TextField select fullWidth required label="Merchant Subscription Plan"
                value={subscriptionPlanValue}
                disabled={!merchantPlansReady || merchantPlansLoading}
                error={missingSubPlan}
                onChange={(e) => patch({ partnerSubscriptionPlanId: e.target.value })}
                helperText={
                  missingSubPlan
                    ? 'Required for subscription-based revenue'
                    : 'Flat-fee plan — commission waived (create under Transport → Partner Subscription Plans, type Merchant)'
                }>
                <MenuItem value="">— Select plan —</MenuItem>
                {subscriptionPlanOptions.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12}>
            <Alert severity="info" sx={highContrastInfoSx}>
              <Typography variant="body2" sx={{ color: '#0c4a6e', fontWeight: 700 }}>
                Pending restaurants are hidden from customer apps until status is <strong>Approved</strong>.
                Blocked restaurants are deactivated automatically.
              </Typography>
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                p: 1.75,
                borderRadius: 1.5,
                border: 1,
                borderColor: 'divider',
                bgcolor: alpha('#0f172a', 0.02),
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2" fontWeight={800}>
                  Compliance documents (required before Approved)
                </Typography>
                <Button size="small" variant="outlined" onClick={() => setTab(5)}>
                  Open Documents tab
                </Button>
              </Stack>
              {!form.merchantId && (
                <Typography variant="body2" color="text.primary" fontWeight={600} sx={{ mt: 1 }}>
                  Link a merchant on Basic Details to load GST / FSSAI / Trade License / Owner ID status.
                </Typography>
              )}
              {form.merchantId && !documentAvailability && (
                <Typography variant="body2" color="text.primary" fontWeight={600} sx={{ mt: 1 }}>
                  Loading merchant document status…
                </Typography>
              )}
              {form.merchantId && documentAvailability && (
                <>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1, mb: 1 }}>
                    <Chip size="small" color="success" variant="outlined" label={`${documentAvailability.approvedCount} approved`} />
                    <Chip size="small" color="warning" variant="outlined" label={`${documentAvailability.pendingCount} pending`} />
                    <Chip size="small" color="default" variant="outlined" label={`${documentAvailability.missingCount} missing`} />
                    <Chip size="small" color="error" variant="outlined" label={`${documentAvailability.rejectedCount} rejected`} />
                  </Stack>
                  <Grid container spacing={1}>
                    {documentSlots.map(({ label, slot }) => (
                      <Grid item xs={12} sm={6} key={label}>
                        <Box
                          sx={{
                            p: 1.25,
                            borderRadius: 1,
                            border: 1,
                            borderColor: slot.status === 'approved' ? 'success.light' : 'divider',
                            bgcolor: 'background.paper',
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Typography variant="body2" fontWeight={800}>{label}</Typography>
                            <Chip
                              size="small"
                              label={formatMerchantDocumentStatus(slot.status)}
                              color={merchantDocumentStatusColor(slot.status)}
                              variant={slot.status === 'approved' ? 'filled' : 'outlined'}
                            />
                          </Stack>
                          <Typography variant="body2" fontWeight={700} sx={{ mt: 0.75 }} color="text.primary">
                            {slot.documentNumber || 'No document number on file'}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  <Typography variant="body2" color="text.primary" fontWeight={600} sx={{ mt: 1.25 }}>
                    {documentAvailability.hasApprovedGstOrFssai
                      ? 'Approved GST/FSSAI numbers are ready — use Documents tab to preview and Apply into Business fields.'
                      : 'Apply GST/FSSAI stays disabled until at least one GST or FSSAI document is approved with a number.'}
                  </Typography>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      )}

      {tab === 5 && (
        <Stack spacing={2}>
          <Alert severity="info" sx={highContrastInfoSx}>
            <Typography variant="body2" sx={{ color: '#0c4a6e', fontWeight: 700 }}>
              Review GST, FSSAI, Trade License, and Owner ID status below before applying numbers to the Business tab.
              Apply is enabled only when an approved GST or FSSAI document number is available.
            </Typography>
          </Alert>
          {form.merchantId && (
            <Box
              sx={{
                p: 1.75,
                borderRadius: 1.5,
                border: 1,
                borderColor: 'divider',
                bgcolor: alpha('#0f172a', 0.02),
              }}
            >
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
                Linked merchant
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 0.25 }}>
                {linkedMerchantLabel}
              </Typography>
              {linkedMerchant && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                  {[linkedMerchant.phone, linkedMerchant.email].filter(Boolean).join(' · ')}
                </Typography>
              )}
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                <Chip size="small" label="Merchant" variant="outlined" />
                <Chip size="small" label={`ID …${form.merchantId.slice(-6)}`} variant="outlined" />
                {documentAvailability && (
                  <Chip
                    size="small"
                    color={documentAvailability.hasApprovedGstOrFssai ? 'success' : 'warning'}
                    label={
                      documentAvailability.hasApprovedGstOrFssai
                        ? `${documentAvailability.approvedCount} approved · ready to apply`
                        : `${documentAvailability.missingCount} missing · ${documentAvailability.pendingCount} pending · ${documentAvailability.rejectedCount} rejected`
                    }
                  />
                )}
              </Stack>
            </Box>
          )}
          {complianceApplyNotice && (
            <Alert severity={complianceApplyNotice.severity} onClose={() => setComplianceApplyNotice(null)} sx={readableAlertSx}>
              {complianceApplyNotice.message}
            </Alert>
          )}
          {!form.merchantId && (
            <Alert severity="warning" sx={readableAlertSx}>
              Select a merchant account on the Basic tab to check document status and apply GST / FSSAI.
            </Alert>
          )}
          {form.merchantId && documentAvailability && (
            <Box>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom color="text.primary">
                Merchant document status
              </Typography>
              <Typography variant="body2" color="text.primary" fontWeight={600} sx={{ mb: 1.25 }}>
                {documentAvailability.approvedCount} approved · {documentAvailability.pendingCount} pending ·{' '}
                {documentAvailability.missingCount} missing · {documentAvailability.rejectedCount} rejected
              </Typography>
              <Grid container spacing={1.25}>
                {documentSlots.map(({ label, slot }) => (
                  <Grid item xs={12} sm={6} key={label}>
                    <Box
                      sx={{
                        p: 1.5,
                        height: '100%',
                        borderRadius: 1.5,
                        border: 1,
                        borderColor:
                          slot.status === 'approved'
                            ? 'success.main'
                            : slot.status === 'rejected'
                              ? 'error.light'
                              : slot.status === 'pending'
                                ? 'warning.light'
                                : 'divider',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle2" fontWeight={800}>{label}</Typography>
                        <Chip
                          size="small"
                          label={formatMerchantDocumentStatus(slot.status)}
                          color={merchantDocumentStatusColor(slot.status)}
                          variant={slot.status === 'approved' ? 'filled' : 'outlined'}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Document number
                      </Typography>
                      <Typography variant="body1" fontWeight={800} color="text.primary" sx={{ wordBreak: 'break-all' }}>
                        {slot.documentNumber || '—'}
                      </Typography>
                      {slot.approved && (
                        <Typography variant="caption" color="success.dark" fontWeight={700} display="block" sx={{ mt: 0.5 }}>
                          Approved — eligible to apply
                        </Typography>
                      )}
                      {slot.status === 'pending' && (
                        <Typography variant="caption" color="warning.dark" fontWeight={700} display="block" sx={{ mt: 0.5 }}>
                          Awaiting admin review — cannot apply yet
                        </Typography>
                      )}
                      {slot.status === 'missing' && (
                        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mt: 0.5 }}>
                          Not uploaded — use Onboarding Hub or Review
                        </Typography>
                      )}
                      {slot.status === 'rejected' && (
                        <Typography variant="caption" color="error.dark" fontWeight={700} display="block" sx={{ mt: 0.5 }}>
                          Rejected or expired — re-upload required
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
              {!documentAvailability.hasApprovedGstOrFssai && (
                <Alert severity="warning" sx={{ ...readableAlertSx, mt: 1.5 }}>
                  No approved GST or FSSAI numbers yet. Upload and approve documents (Review or Onboarding Hub) before Apply is enabled.
                </Alert>
              )}
            </Box>
          )}
          {approvedComplianceSyncPreview.length > 0 && (
            <Alert severity="info" sx={readableAlertSx}>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom color="text.primary">
                Apply preview — values that will synchronize
              </Typography>
              <Typography variant="body2" color="text.primary" fontWeight={600} sx={{ mb: 1 }}>
                These approved document numbers are available to copy into the Business tab:
              </Typography>
              <Stack spacing={0.5}>
                {approvedComplianceSyncPreview.map((row) => (
                  <Typography key={row.field} variant="body2" color="text.primary" fontWeight={700}>
                    {row.label}: {row.current} → {row.next}
                    {row.changed ? ' (will update)' : ' (already matches)'}
                  </Typography>
                ))}
              </Stack>
              {complianceApplyPreview.length === 0 && (
                <Typography variant="body2" color="text.primary" fontWeight={600} sx={{ mt: 1 }}>
                  Nothing new to apply — restaurant fields already match approved documents.
                </Typography>
              )}
            </Alert>
          )}
          <Box>
            <Button
              variant="contained"
              color="primary"
              disabled={!form.merchantId || !documentAvailability?.hasApprovedGstOrFssai || complianceApplyPreview.length === 0}
              onClick={requestApplyApprovedDocuments}
            >
              {documentAvailability?.hasApprovedGstOrFssai
                ? complianceApplyPreview.length > 0
                  ? `Apply GST / FSSAI (${complianceApplyPreview.map((r) => r.label).join(', ')})`
                  : 'Apply GST / FSSAI (already in sync)'
                : 'Apply GST / FSSAI (no approved documents yet)'}
            </Button>
            <Typography variant="body2" color="text.primary" fontWeight={600} sx={{ mt: 0.75 }}>
              {!form.merchantId
                ? 'Select a merchant first.'
                : !documentAvailability?.hasApprovedGstOrFssai
                  ? 'Disabled until at least one approved GST or FSSAI document number is available (status shown above).'
                  : complianceApplyPreview.length === 0
                    ? 'Disabled because approved values already match Business fields.'
                    : 'Enabled — confirm dialog shows the exact GST / FSSAI / Trade License values before update.'}
            </Typography>
          </Box>
          <Box>
            <Button
              variant="outlined"
              disabled={!form.merchantId}
              onClick={() => setMerchantDocumentsOpen(true)}
            >
              {!form.merchantId
                ? 'Review merchant documents (select merchant first)'
                : documentAvailability
                  ? documentAvailability.attentionCount > 0
                    ? `Review merchant documents (${documentAvailability.pendingCount} pending · ${documentAvailability.missingCount} missing · ${documentAvailability.rejectedCount} rejected)`
                    : `Review merchant documents (${documentAvailability.approvedCount} approved · all clear)`
                  : 'Review merchant documents'}
            </Button>
            <Typography variant="body2" color="text.primary" fontWeight={600} sx={{ mt: 0.75 }}>
              {!form.merchantId
                ? 'Select a merchant account on the Basic tab to open their document queue.'
                : documentAvailability
                  ? documentAvailability.attentionCount > 0
                    ? 'Opens this merchant’s document queue — documents still need upload or review.'
                    : 'Opens this merchant’s document queue — required slots are approved.'
                  : 'Loading document availability for this merchant…'}
            </Typography>
          </Box>
          {form.merchantId && (
            <Button
              component={RouterLink}
              to={documentsUrl}
              target="_blank"
              variant="outlined"
              endIcon={<OpenInNew />}
              sx={{ alignSelf: 'flex-start' }}
            >
              Open {linkedMerchantLabel} in Partners (new tab)
            </Button>
          )}
          <Box>
            <Button component={RouterLink} to="/onboarding" target="_blank" variant="outlined" endIcon={<OpenInNew />}>
              {documentAvailability && documentAvailability.attentionCount > 0
                ? `Open partner onboarding hub (${documentAvailability.attentionCount} docs need attention)`
                : documentAvailability
                  ? 'Open partner onboarding hub (KYC complete — track / re-upload if needed)'
                  : 'Open partner onboarding hub (complete / track KYC uploads)'}
            </Button>
            <Typography variant="body2" color="text.primary" fontWeight={600} sx={{ mt: 0.75 }}>
              {documentAvailability && documentAvailability.attentionCount > 0
                ? `Use onboarding when documents are missing (${documentAvailability.missingCount}), pending (${documentAvailability.pendingCount}), or rejected (${documentAvailability.rejectedCount}).`
                : 'Use onboarding for partners who still need to submit or replace KYC uploads.'}
            </Typography>
          </Box>
        </Stack>
      )}

      <ConfirmDialog
        open={applyComplianceConfirmOpen}
        title="Apply approved document values?"
        confirmLabel="Apply to restaurant"
        confirmColor="primary"
        message={
          complianceApplyPreview.length
            ? [
              `Apply approved values for ${linkedMerchantLabel}?`,
              '',
              'Only these Business tab fields will be updated:',
              ...complianceApplyPreview.map((row) => `• ${row.label}: ${row.current} → ${row.next}`),
              '',
              'Restaurant name, contact, location, timing, and approval settings will not be changed.',
            ].join('\n')
            : 'No approved document values are available to apply.'
        }
        onCancel={() => setApplyComplianceConfirmOpen(false)}
        onConfirm={confirmApplyApprovedDocuments}
      />

      <PartnerDocumentsPanel
        open={merchantDocumentsOpen}
        onClose={() => setMerchantDocumentsOpen(false)}
        partner={linkedMerchant as PartnerRow | null}
        entityType="merchant"
      />
    </FormDrawer>
  );
}
